import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import isoWeek from 'dayjs/plugin/isoWeek'
import weekday from 'dayjs/plugin/weekday'
import type { Query, QueryKey } from 'react-query'
import { DAILY, WEEKLY } from 'screens/Rostering/constants'
import {
  areSameWeek,
  getWeekBoundaries
} from 'screens/Rostering/utils.datetime'
import { resourceToQueryKeyMap } from './constants'
import type {
  InvalidateMessage,
  SocketInvalidateMessage
} from './useQueryInvalidation'

dayjs.extend(weekday)
dayjs.extend(isoWeek)
dayjs.extend(isBetween)

// The socket payload is untrusted at runtime, so anything that is not a string
// is treated as absent rather than being passed to `.toLowerCase()`.
const toLowerCaseString = (value: unknown) =>
  typeof value === 'string' ? value.toLowerCase() : undefined

/**
 * The rota backend emits the interval and dates nested under `params`
 * (`{ changedResource, params: { type, day | startDate } }`), while older
 * payloads used a flat shape. Flatten both into a single `InvalidateMessage`.
 */
export const normaliseInvalidateMessage = (
  message: SocketInvalidateMessage
): InvalidateMessage => {
  const params =
    typeof message?.params === 'object' && message?.params !== null
      ? message.params
      : undefined

  return {
    changedResource: message?.changedResource,
    changedInterval: params?.type ?? message?.changedInterval,
    day: params?.day ?? message?.day,
    startDate: params?.startDate ?? message?.startDate,
    endDate: params?.endDate ?? message?.endDate
  }
}

export const getQueryInvalidationPredicate =
  ({
    changedResource,
    changedInterval,
    startDate,
    endDate,
    day
  }: InvalidateMessage) =>
  (query: Query<unknown, unknown, unknown, QueryKey>) => {
    const queryKeysToInvalidate = resourceToQueryKeyMap[changedResource]
    const dateToInvalidate = day ?? startDate
    const intervalTypeToInvalidate = toLowerCaseString(changedInterval)
    const isQueryKeyArray = Array.isArray(query?.queryKey)

    const queryKey = isQueryKeyArray ? query?.queryKey?.[0] : query?.queryKey
    const date = isQueryKeyArray ? query?.queryKey?.[2] : undefined
    const intervalType = isQueryKeyArray
      ? toLowerCaseString(query?.queryKey?.[3])
      : undefined
    const dayjsDate = dayjs(date as string)

    const shouldSkipQueryKey =
      typeof queryKey !== 'string' || !queryKeysToInvalidate?.includes(queryKey)

    if (shouldSkipQueryKey) {
      return false
    }

    // If no date was provided invalidate everything
    if (!dateToInvalidate) {
      return queryKeysToInvalidate?.includes(queryKey)
    }

    if (!intervalType && startDate && endDate) {
      return dayjsDate.isBetween(startDate, endDate)
    }

    const areIntervalsInvalid =
      typeof intervalType !== 'string' ||
      typeof intervalTypeToInvalidate !== 'string' ||
      ![DAILY, WEEKLY].includes(intervalType) ||
      ![DAILY, WEEKLY].includes(intervalTypeToInvalidate)

    if (areIntervalsInvalid) {
      return false
    }

    if (intervalType === DAILY && intervalTypeToInvalidate === DAILY) {
      const isSameDay = dayjs(dateToInvalidate)
        .startOf('day')
        .isSame(dayjsDate.startOf('day'))

      if (isSameDay) {
        return true
      }
    }

    if (intervalType === DAILY && intervalTypeToInvalidate === WEEKLY) {
      const [monday, sunday] = getWeekBoundaries(dateToInvalidate)

      // if week to invalidate includes date
      if (dayjsDate.isBetween(monday, sunday)) {
        return true
      }
    }

    if (intervalType === WEEKLY && intervalTypeToInvalidate === DAILY) {
      const [monday, sunday] = getWeekBoundaries(dayjsDate)

      // if week to invalidate includes date
      if (dayjs(dateToInvalidate).isBetween(monday, sunday)) {
        return true
      }
    }

    if (intervalType === WEEKLY && intervalTypeToInvalidate === WEEKLY) {
      if (areSameWeek(dayjsDate, dateToInvalidate)) {
        return true
      }
    }

    return false
  }
